import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Plus, Edit, Trash2, Eye, Search, Save, X, Upload, FileText,
  Image, Lock, Tag, BookOpen,
  AlertCircle, CheckCircle, ArrowLeft, Folder, Bold, Italic,
  Link, List, ListOrdered, Type, Columns, Minus, ArrowUp,
  ArrowDown, Settings, GripVertical, FileUp, AlignLeft,
  AlignCenter, AlignRight, Maximize, LayoutPanelLeft
} from 'lucide-react';
import { AuthContext } from '../App';
import { useTheme } from '../contexts/ThemeContext';
import './Blog.css';

const API_URL = process.env.REACT_APP_API_URL;

// ── Block Utilities ───────────────────────────────────────────────────

const generateBlockId = () => 'block-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);

const createBlock = (type, content) => {
  const defaults = {
    texto: { id: generateBlockId(), type: 'texto', content: '', settings: { fontSize: 'normal' } },
    imagen: { id: generateBlockId(), type: 'imagen', content: { url: '', caption: '', align: 'center', width: 100 }, settings: {} },
    'imagen-texto': { id: generateBlockId(), type: 'imagen-texto', content: { template: 'img-left', ratio: '40-60', image: { url: '', caption: '' }, text: '' }, settings: {} },
    'dos-columnas': { id: generateBlockId(), type: 'dos-columnas', content: { left: '', right: '', ratio: '50-50' }, settings: {} },
    documento: { id: generateBlockId(), type: 'documento', content: { url: '', nombre: '', tamano: 0 }, settings: {} },
    separador: { id: generateBlockId(), type: 'separador', content: '', settings: {} },
  };
  const block = defaults[type] || defaults.texto;
  if (content) block.content = content;
  return block;
};

const BLOCKS_PREFIX = '<!-- BLOCKS_JSON:';
const BLOCKS_SUFFIX = ' -->';

const encodeBlocks = (blocks) => {
  const json = JSON.stringify(blocks);
  return BLOCKS_PREFIX + btoa(unescape(encodeURIComponent(json))) + BLOCKS_SUFFIX;
};

const decodeBlocks = (contenido) => {
  if (!contenido || !contenido.startsWith(BLOCKS_PREFIX)) return null;
  try {
    const endIdx = contenido.indexOf(BLOCKS_SUFFIX);
    if (endIdx === -1) return null;
    const b64 = contenido.substring(BLOCKS_PREFIX.length, endIdx);
    const json = decodeURIComponent(escape(atob(b64)));
    return JSON.parse(json);
  } catch (e) {
    console.error('Error decoding blocks:', e);
    return null;
  }
};

const fontSizeMap = {
  small: '0.88rem',
  normal: '1rem',
  large: '1.15rem',
  xl: '1.3rem',
};

const ratioToGrid = (ratio) => {
  const map = {
    '50-50': '1fr 1fr',
    '40-60': '2fr 3fr',
    '60-40': '3fr 2fr',
    '30-70': '3fr 7fr',
    '70-30': '7fr 3fr',
  };
  return map[ratio] || '1fr 1fr';
};

const blocksToHtml = (blocks) => {
  return blocks.map(block => {
    switch (block.type) {
      case 'texto': {
        const size = (block.settings && block.settings.fontSize) || 'normal';
        return `<div class="blog-block blog-block-text blog-text-${size}">${block.content || ''}</div>`;
      }
      case 'imagen': {
        const c = block.content || {};
        const alignClass = c.align ? ` align-${c.align}` : ' align-center';
        const widthVal = c.width || 100;
        let html = `<div class="blog-block blog-block-image${alignClass}" style="max-width:${widthVal}%">`;
        if (c.url) html += `<img src="${c.url}" alt="${c.caption || ''}" />`;
        if (c.caption) html += `<p class="blog-image-caption">${c.caption}</p>`;
        html += '</div>';
        return html;
      }
      case 'imagen-texto': {
        const c = block.content || {};
        const template = c.template || 'img-left';
        const ratio = c.ratio || '40-60';
        let html = `<div class="blog-block blog-layout-img-text layout-${template} ratio-${ratio}">`;
        html += `<div class="layout-image">`;
        if (c.image && c.image.url) {
          html += `<img src="${c.image.url}" alt="${(c.image && c.image.caption) || ''}" />`;
          if (c.image.caption) html += `<p class="blog-image-caption">${c.image.caption}</p>`;
        }
        html += `</div>`;
        html += `<div class="layout-text">${c.text || ''}</div>`;
        html += '</div>';
        return html;
      }
      case 'dos-columnas': {
        const c = block.content || {};
        const ratio = c.ratio || '50-50';
        return `<div class="blog-block blog-block-columns ratio-${ratio} with-separator"><div class="blog-column-left">${c.left || ''}</div><div class="blog-column-right">${c.right || ''}</div></div>`;
      }
      case 'documento': {
        const c = block.content || {};
        return `<div class="blog-block blog-block-document"><a href="${c.url}" target="_blank" rel="noopener noreferrer"><span class="doc-icon">&#128196;</span> ${c.nombre || 'Documento'} ${c.tamano ? `<small>(${(c.tamano / 1024).toFixed(0)} KB)</small>` : ''}</a></div>`;
      }
      case 'separador':
        return '<div class="blog-block blog-block-separator"><hr /></div>';
      default:
        return '';
    }
  }).join('\n');
};

const parseContentToBlocks = (contenido) => {
  if (!contenido) return [createBlock('texto')];
  const decoded = decodeBlocks(contenido);
  if (decoded && Array.isArray(decoded)) {
    // Migrate old blocks to new format
    return decoded.map(block => {
      if (block.type === 'texto' && (!block.settings || block.settings.fontSize === undefined)) {
        return { ...block, settings: { ...block.settings, fontSize: 'normal' } };
      }
      if (block.type === 'imagen' && block.content && block.content.width === undefined) {
        return { ...block, content: { ...block.content, width: 100 } };
      }
      if (block.type === 'dos-columnas' && block.content && block.content.ratio === undefined) {
        return { ...block, content: { ...block.content, ratio: '50-50' } };
      }
      return block;
    });
  }
  return [createBlock('texto', contenido)];
};

// ── Rich Text Toolbar Component ───────────────────────────────────────

const RichTextToolbar = ({ editorRef }) => {
  const exec = (command, value) => {
    if (editorRef && editorRef.current) {
      editorRef.current.focus();
    }
    document.execCommand(command, false, value || null);
  };

  const handleLink = () => {
    const url = prompt('Introduce la URL del enlace:', 'https://');
    if (url) exec('createLink', url);
  };

  const btnStyle = {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: 32, height: 32, border: '1px solid #e2e8f0', borderRadius: 4,
    background: 'white', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600,
    color: '#4a5568', transition: 'all 0.15s',
  };

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, padding: '6px 8px', background: '#f8fafb', borderBottom: '1px solid #e2e8f0', borderRadius: '8px 8px 0 0' }}>
      <button type="button" style={btnStyle} onMouseDown={e => { e.preventDefault(); exec('bold'); }} title="Negrita"><Bold size={14} /></button>
      <button type="button" style={btnStyle} onMouseDown={e => { e.preventDefault(); exec('italic'); }} title="Cursiva"><Italic size={14} /></button>
      <div style={{ width: 1, background: '#e2e8f0', margin: '0 4px' }} />
      <button type="button" style={btnStyle} onMouseDown={e => { e.preventDefault(); exec('formatBlock', 'h2'); }} title="Encabezado H2"><span style={{ fontSize: 11 }}>H2</span></button>
      <button type="button" style={btnStyle} onMouseDown={e => { e.preventDefault(); exec('formatBlock', 'h3'); }} title="Encabezado H3"><span style={{ fontSize: 11 }}>H3</span></button>
      <button type="button" style={btnStyle} onMouseDown={e => { e.preventDefault(); exec('formatBlock', 'p'); }} title="Parrafo normal"><Type size={14} /></button>
      <div style={{ width: 1, background: '#e2e8f0', margin: '0 4px' }} />
      <button type="button" style={btnStyle} onMouseDown={e => { e.preventDefault(); handleLink(); }} title="Enlace"><Link size={14} /></button>
      <button type="button" style={btnStyle} onMouseDown={e => { e.preventDefault(); exec('insertUnorderedList'); }} title="Lista sin orden"><List size={14} /></button>
      <button type="button" style={btnStyle} onMouseDown={e => { e.preventDefault(); exec('insertOrderedList'); }} title="Lista ordenada"><ListOrdered size={14} /></button>
    </div>
  );
};

// ── Editable Content Component ────────────────────────────────────────

const EditableContent = ({ value, onChange, placeholder, minHeight = 120 }) => {
  const ref = useRef(null);
  const isInternalUpdate = useRef(false);

  useEffect(() => {
    if (ref.current && !isInternalUpdate.current) {
      if (ref.current.innerHTML !== value) {
        ref.current.innerHTML = value || '';
      }
    }
    isInternalUpdate.current = false;
  }, [value]);

  const handleInput = useCallback(() => {
    isInternalUpdate.current = true;
    if (onChange) onChange(ref.current.innerHTML);
  }, [onChange]);

  return (
    <div>
      <RichTextToolbar editorRef={ref} />
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        data-placeholder={placeholder || 'Escribe aqui...'}
        style={{
          minHeight,
          padding: '12px 14px',
          border: '1px solid #e2e8f0',
          borderTop: 'none',
          borderRadius: '0 0 8px 8px',
          outline: 'none',
          fontSize: '0.95rem',
          lineHeight: 1.7,
          color: '#374151',
          fontFamily: 'inherit',
          overflowY: 'auto',
        }}
      />
    </div>
  );
};

// ── Mini Preview Diagrams for Block Menu ──────────────────────────────

const MiniPreviewText = () => (
  <div style={{ width: 48, height: 36, display: 'flex', flexDirection: 'column', gap: 3, justifyContent: 'center', padding: 4 }}>
    <div style={{ height: 3, background: '#94a3b8', borderRadius: 2, width: '100%' }} />
    <div style={{ height: 3, background: '#94a3b8', borderRadius: 2, width: '85%' }} />
    <div style={{ height: 3, background: '#94a3b8', borderRadius: 2, width: '70%' }} />
  </div>
);

const MiniPreviewImage = () => (
  <div style={{ width: 48, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <div style={{ width: 36, height: 28, background: '#c4b5fd', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Image size={14} color="#7c3aed" />
    </div>
  </div>
);

const MiniPreviewImgTextLeft = () => (
  <div style={{ width: 48, height: 36, display: 'flex', gap: 2, alignItems: 'center', padding: 2 }}>
    <div style={{ width: 18, height: 28, background: '#c4b5fd', borderRadius: 3 }} />
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2, justifyContent: 'center' }}>
      <div style={{ height: 2, background: '#94a3b8', borderRadius: 1, width: '100%' }} />
      <div style={{ height: 2, background: '#94a3b8', borderRadius: 1, width: '80%' }} />
      <div style={{ height: 2, background: '#94a3b8', borderRadius: 1, width: '60%' }} />
    </div>
  </div>
);

const MiniPreviewImgTextRight = () => (
  <div style={{ width: 48, height: 36, display: 'flex', gap: 2, alignItems: 'center', padding: 2 }}>
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2, justifyContent: 'center' }}>
      <div style={{ height: 2, background: '#94a3b8', borderRadius: 1, width: '100%' }} />
      <div style={{ height: 2, background: '#94a3b8', borderRadius: 1, width: '80%' }} />
      <div style={{ height: 2, background: '#94a3b8', borderRadius: 1, width: '60%' }} />
    </div>
    <div style={{ width: 18, height: 28, background: '#c4b5fd', borderRadius: 3 }} />
  </div>
);

const MiniPreviewTwoColumns = () => (
  <div style={{ width: 48, height: 36, display: 'flex', gap: 2, alignItems: 'center', padding: 2 }}>
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <div style={{ height: 2, background: '#f59e0b', borderRadius: 1, width: '100%' }} />
      <div style={{ height: 2, background: '#f59e0b', borderRadius: 1, width: '70%' }} />
    </div>
    <div style={{ width: 1, background: '#d1d5db', height: '80%' }} />
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <div style={{ height: 2, background: '#f59e0b', borderRadius: 1, width: '100%' }} />
      <div style={{ height: 2, background: '#f59e0b', borderRadius: 1, width: '70%' }} />
    </div>
  </div>
);

const MiniPreviewDocument = () => (
  <div style={{ width: 48, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <div style={{ width: 24, height: 28, background: '#fecaca', borderRadius: 3, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <FileText size={12} color="#dc2626" />
    </div>
  </div>
);

const MiniPreviewSeparator = () => (
  <div style={{ width: 48, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px' }}>
    <div style={{ width: '100%', height: 2, background: '#d1d5db', borderRadius: 1 }} />
  </div>
);

// ── Add Block Menu (Visual Grid) ─────────────────────────────────────

const AddBlockMenu = ({ onAdd }) => {
  const [open, setOpen] = useState(false);

  const groups = [
    {
      label: 'Contenido',
      items: [
        { type: 'texto', label: 'Texto', desc: 'Parrafo con formato', preview: <MiniPreviewText /> },
        { type: 'imagen', label: 'Imagen', desc: 'Imagen con pie de foto', preview: <MiniPreviewImage /> },
      ],
    },
    {
      label: 'Disenos',
      items: [
        { type: 'imagen-texto', variant: 'img-left', label: 'Imagen + Texto', desc: 'Imagen a la izquierda', preview: <MiniPreviewImgTextLeft /> },
        { type: 'imagen-texto', variant: 'img-right', label: 'Texto + Imagen', desc: 'Imagen a la derecha', preview: <MiniPreviewImgTextRight /> },
        { type: 'dos-columnas', label: 'Dos columnas', desc: 'Texto en dos columnas', preview: <MiniPreviewTwoColumns /> },
      ],
    },
    {
      label: 'Otros',
      items: [
        { type: 'documento', label: 'Documento', desc: 'Adjuntar PDF o doc', preview: <MiniPreviewDocument /> },
        { type: 'separador', label: 'Separador', desc: 'Linea divisoria', preview: <MiniPreviewSeparator /> },
      ],
    },
  ];

  const handleSelect = (item) => {
    if (item.type === 'imagen-texto') {
      const block = createBlock('imagen-texto');
      block.content.template = item.variant || 'img-left';
      onAdd(block.type, block);
    } else {
      onAdd(item.type);
    }
    setOpen(false);
  };

  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '8px 0', position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          background: open ? '#3cbf9a' : 'white',
          color: open ? 'white' : '#3cbf9a',
          border: '2px dashed #3cbf9a', borderRadius: 20,
          padding: '6px 16px', cursor: 'pointer', fontSize: '0.82rem',
          fontWeight: 600, transition: 'all 0.2s',
        }}
      >
        <Plus size={14} /> Agregar bloque
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)',
          background: 'white', border: '1px solid #e2e8f0', borderRadius: 12,
          boxShadow: '0 8px 30px rgba(0,0,0,0.12)', padding: '12px 14px', zIndex: 50,
          minWidth: 360,
        }}>
          {groups.map((group, gi) => (
            <div key={gi} style={{ marginBottom: gi < groups.length - 1 ? 12 : 0 }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6, paddingLeft: 4 }}>
                {group.label}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 6 }}>
                {group.items.map((item, ii) => (
                  <button
                    key={`${item.type}-${ii}`}
                    type="button"
                    onClick={() => handleSelect(item)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      padding: '8px 10px', border: '1px solid #f1f5f9',
                      borderRadius: 10, background: '#fafbfc', cursor: 'pointer',
                      textAlign: 'left', transition: 'all 0.15s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#f0fdf4'; e.currentTarget.style.borderColor = '#bbf7d0'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = '#fafbfc'; e.currentTarget.style.borderColor = '#f1f5f9'; }}
                  >
                    <div style={{ flexShrink: 0, border: '1px solid #e2e8f0', borderRadius: 6, overflow: 'hidden', background: 'white' }}>
                      {item.preview}
                    </div>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '0.82rem', color: '#374151' }}>{item.label}</div>
                      <div style={{ fontSize: '0.72rem', color: '#94a3b8', lineHeight: 1.3 }}>{item.desc}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Block Controls ────────────────────────────────────────────────────

const BlockControls = ({ index, total, onMoveUp, onMoveDown, onDelete }) => (
  <div style={{
    position: 'absolute', top: 4, right: 4, display: 'flex', gap: 3,
    opacity: 0, transition: 'opacity 0.15s', zIndex: 10,
  }} className="block-controls">
    {index > 0 && (
      <button type="button" onClick={onMoveUp} title="Mover arriba"
        style={{ width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #e2e8f0', borderRadius: 6, background: 'white', cursor: 'pointer', color: '#4a5568' }}>
        <ArrowUp size={13} />
      </button>
    )}
    {index < total - 1 && (
      <button type="button" onClick={onMoveDown} title="Mover abajo"
        style={{ width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #e2e8f0', borderRadius: 6, background: 'white', cursor: 'pointer', color: '#4a5568' }}>
        <ArrowDown size={13} />
      </button>
    )}
    <button type="button" onClick={onDelete} title="Eliminar bloque"
      style={{ width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #fecaca', borderRadius: 6, background: '#fff5f5', cursor: 'pointer', color: '#e53e3e' }}>
      <Trash2 size={13} />
    </button>
  </div>
);

// ── Font Size Selector ───────────────────────────────────────────────

const FontSizeSelector = ({ value, onChange }) => {
  const sizes = [
    { key: 'small', label: 'Pequeno' },
    { key: 'normal', label: 'Normal' },
    { key: 'large', label: 'Grande' },
    { key: 'xl', label: 'Muy grande' },
  ];
  return (
    <div style={{ display: 'flex', gap: 2 }}>
      {sizes.map(s => (
        <button
          key={s.key}
          type="button"
          onClick={() => onChange(s.key)}
          style={{
            padding: '2px 8px', border: '1px solid',
            borderColor: value === s.key ? '#3cbf9a' : '#e2e8f0',
            borderRadius: 4, background: value === s.key ? '#f0fdf4' : 'white',
            color: value === s.key ? '#059669' : '#718096',
            cursor: 'pointer', fontSize: '0.7rem', fontWeight: 600, transition: 'all 0.15s',
            lineHeight: '20px',
          }}
        >
          {s.label}
        </button>
      ))}
    </div>
  );
};

// ── Ratio Selector ───────────────────────────────────────────────────

const RatioSelector = ({ value, onChange }) => {
  const ratios = [
    { key: '30-70', left: 30, right: 70 },
    { key: '40-60', left: 40, right: 60 },
    { key: '50-50', left: 50, right: 50 },
    { key: '60-40', left: 60, right: 40 },
    { key: '70-30', left: 70, right: 30 },
  ];
  return (
    <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
      <span style={{ fontSize: '0.72rem', color: '#94a3b8', fontWeight: 600, marginRight: 4 }}>Proporcion:</span>
      {ratios.map(r => (
        <button
          key={r.key}
          type="button"
          onClick={() => onChange(r.key)}
          title={`${r.left}/${r.right}`}
          style={{
            display: 'flex', width: 40, height: 24, border: '2px solid',
            borderColor: value === r.key ? '#3cbf9a' : '#e2e8f0',
            borderRadius: 4, overflow: 'hidden', cursor: 'pointer',
            background: 'white', padding: 0, transition: 'border-color 0.15s',
          }}
        >
          <div style={{ width: `${r.left}%`, height: '100%', background: value === r.key ? '#3cbf9a' : '#cbd5e0' }} />
          <div style={{ width: 1, height: '100%', background: 'white' }} />
          <div style={{ width: `${r.right}%`, height: '100%', background: value === r.key ? '#6ee7b7' : '#e2e8f0' }} />
        </button>
      ))}
    </div>
  );
};

// ── Individual Block Editors ──────────────────────────────────────────

const TextoBlockEditor = ({ block, onUpdate }) => {
  const handleChange = useCallback((html) => {
    onUpdate({ ...block, content: html });
  }, [block, onUpdate]);

  const fontSize = (block.settings && block.settings.fontSize) || 'normal';

  return (
    <div>
      <div style={{ marginBottom: 8 }}>
        <FontSizeSelector
          value={fontSize}
          onChange={(size) => onUpdate({ ...block, settings: { ...block.settings, fontSize: size } })}
        />
      </div>
      <EditableContent
        value={block.content || ''}
        onChange={handleChange}
        placeholder="Escribe el contenido..."
        minHeight={100}
      />
    </div>
  );
};

const ImagenBlockEditor = ({ block, onUpdate, token }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const content = block.content || { url: '', caption: '', align: 'center', width: 100 };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API_URL}/api/blog/admin/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Error al subir');
      onUpdate({ ...block, content: { ...content, url: data.url } });
    } catch (err) {
      alert('Error al subir imagen: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  const alignOptions = [
    { value: 'left', icon: <AlignLeft size={14} />, label: 'Izquierda' },
    { value: 'center', icon: <AlignCenter size={14} />, label: 'Centro' },
    { value: 'right', icon: <AlignRight size={14} />, label: 'Derecha' },
    { value: 'full', icon: <Maximize size={14} />, label: 'Ancho completo' },
  ];

  const currentWidth = content.width || 100;

  return (
    <div>
      {content.url ? (
        <div style={{ textAlign: content.align === 'right' ? 'right' : content.align === 'left' ? 'left' : 'center' }}>
          <img
            src={content.url}
            alt={content.caption || ''}
            style={{
              maxWidth: `${currentWidth}%`,
              width: content.align === 'full' ? '100%' : 'auto',
              borderRadius: 8, maxHeight: 400, objectFit: 'cover',
            }}
          />
        </div>
      ) : (
        <div
          onClick={() => fileRef.current && fileRef.current.click()}
          style={{
            border: '2px dashed #e2e8f0', borderRadius: 8, padding: '40px 20px',
            textAlign: 'center', cursor: 'pointer', color: '#a0aec0',
            background: '#fafbfc', transition: 'border-color 0.2s',
          }}
          onMouseEnter={e => e.currentTarget.style.borderColor = '#3cbf9a'}
          onMouseLeave={e => e.currentTarget.style.borderColor = '#e2e8f0'}
        >
          {uploading ? (
            <div><div className="blog-spinner" style={{ display: 'inline-block', width: 24, height: 24, borderWidth: 3 }} /> <p style={{ marginTop: 8 }}>Subiendo imagen...</p></div>
          ) : (
            <div>
              <Upload size={32} style={{ marginBottom: 8 }} />
              <p style={{ fontWeight: 500 }}>Haz clic para subir una imagen</p>
              <small>JPG, PNG, GIF, WebP</small>
            </div>
          )}
        </div>
      )}
      <input ref={fileRef} type="file" accept="image/*" onChange={handleUpload} style={{ display: 'none' }} />

      {content.url && (
        <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              type="text"
              value={content.caption || ''}
              onChange={e => onUpdate({ ...block, content: { ...content, caption: e.target.value } })}
              placeholder="Pie de imagen (opcional)"
              style={{ flex: 1, minWidth: 150, padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: '0.85rem', outline: 'none' }}
            />
            <div style={{ display: 'flex', gap: 3 }}>
              {alignOptions.map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => onUpdate({ ...block, content: { ...content, align: opt.value } })}
                  title={opt.label}
                  style={{
                    width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    border: '1px solid', borderColor: content.align === opt.value ? '#3cbf9a' : '#e2e8f0',
                    borderRadius: 6, background: content.align === opt.value ? '#f0fdf4' : 'white',
                    color: content.align === opt.value ? '#3cbf9a' : '#718096', cursor: 'pointer',
                  }}
                >
                  {opt.icon}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => { if (fileRef.current) fileRef.current.click(); }}
              style={{ padding: '6px 12px', border: '1px solid #e2e8f0', borderRadius: 6, background: 'white', cursor: 'pointer', fontSize: '0.82rem', color: '#4a5568' }}
            >
              Cambiar
            </button>
          </div>
          {/* Width slider */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '4px 0' }}>
            <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#718096', whiteSpace: 'nowrap' }}>Ancho:</span>
            <input
              type="range"
              min={25}
              max={100}
              value={currentWidth}
              onChange={e => onUpdate({ ...block, content: { ...content, width: parseInt(e.target.value, 10) } })}
              style={{ flex: 1, accentColor: '#3cbf9a', cursor: 'pointer' }}
            />
            <span style={{ fontSize: '0.82rem', fontWeight: 600, color: '#3cbf9a', minWidth: 40, textAlign: 'right' }}>{currentWidth}%</span>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Imagen-Texto Block Editor (NEW) ──────────────────────────────────

const ImagenTextoBlockEditor = ({ block, onUpdate, token }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const content = block.content || { template: 'img-left', ratio: '40-60', image: { url: '', caption: '' }, text: '' };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API_URL}/api/blog/admin/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Error al subir');
      onUpdate({ ...block, content: { ...content, image: { ...content.image, url: data.url } } });
    } catch (err) {
      alert('Error al subir imagen: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleTextChange = useCallback((html) => {
    onUpdate({ ...block, content: { ...content, text: html } });
  }, [block, content, onUpdate]);

  const template = content.template || 'img-left';
  const ratio = content.ratio || '40-60';

  const imageSection = (
    <div>
      <div style={{ fontSize: '0.72rem', fontWeight: 600, color: '#8b5cf6', marginBottom: 6, textTransform: 'uppercase' }}>Imagen</div>
      {content.image && content.image.url ? (
        <div style={{ position: 'relative' }}>
          <img
            src={content.image.url}
            alt={content.image.caption || ''}
            style={{ width: '100%', borderRadius: 8, maxHeight: 300, objectFit: 'cover' }}
          />
          <div style={{ marginTop: 6, display: 'flex', gap: 6 }}>
            <input
              type="text"
              value={(content.image && content.image.caption) || ''}
              onChange={e => onUpdate({ ...block, content: { ...content, image: { ...content.image, caption: e.target.value } } })}
              placeholder="Pie de imagen"
              style={{ flex: 1, padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: '0.82rem', outline: 'none' }}
            />
            <button
              type="button"
              onClick={() => { if (fileRef.current) fileRef.current.click(); }}
              style={{ padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: 6, background: 'white', cursor: 'pointer', fontSize: '0.78rem', color: '#4a5568' }}
            >
              Cambiar
            </button>
          </div>
        </div>
      ) : (
        <div
          onClick={() => fileRef.current && fileRef.current.click()}
          style={{
            border: '2px dashed #e2e8f0', borderRadius: 8, padding: '30px 16px',
            textAlign: 'center', cursor: 'pointer', color: '#a0aec0',
            background: '#fafbfc', transition: 'border-color 0.2s',
          }}
          onMouseEnter={e => e.currentTarget.style.borderColor = '#3cbf9a'}
          onMouseLeave={e => e.currentTarget.style.borderColor = '#e2e8f0'}
        >
          {uploading ? (
            <div><div className="blog-spinner" style={{ display: 'inline-block', width: 24, height: 24, borderWidth: 3 }} /> <p style={{ marginTop: 6, fontSize: '0.82rem' }}>Subiendo...</p></div>
          ) : (
            <div>
              <Upload size={24} style={{ marginBottom: 6 }} />
              <p style={{ fontWeight: 500, fontSize: '0.85rem' }}>Subir imagen</p>
            </div>
          )}
        </div>
      )}
      <input ref={fileRef} type="file" accept="image/*" onChange={handleImageUpload} style={{ display: 'none' }} />
    </div>
  );

  const textSection = (
    <div>
      <div style={{ fontSize: '0.72rem', fontWeight: 600, color: '#3b82f6', marginBottom: 6, textTransform: 'uppercase' }}>Texto</div>
      <EditableContent
        value={content.text || ''}
        onChange={handleTextChange}
        placeholder="Escribe el texto..."
        minHeight={80}
      />
    </div>
  );

  const gridCols = ratioToGrid(ratio);

  return (
    <div>
      {/* Controls bar */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', marginBottom: 12, padding: '8px 10px', background: '#f8fafb', borderRadius: 8 }}>
        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          <span style={{ fontSize: '0.72rem', color: '#94a3b8', fontWeight: 600, marginRight: 4 }}>Plantilla:</span>
          <button
            type="button"
            onClick={() => onUpdate({ ...block, content: { ...content, template: 'img-left' } })}
            style={{
              display: 'flex', alignItems: 'center', gap: 4,
              padding: '4px 10px', border: '1px solid',
              borderColor: template === 'img-left' ? '#3cbf9a' : '#e2e8f0',
              borderRadius: 6, background: template === 'img-left' ? '#f0fdf4' : 'white',
              color: template === 'img-left' ? '#059669' : '#718096',
              cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600,
            }}
          >
            <MiniPreviewImgTextLeft /> Img izq
          </button>
          <button
            type="button"
            onClick={() => onUpdate({ ...block, content: { ...content, template: 'img-right' } })}
            style={{
              display: 'flex', alignItems: 'center', gap: 4,
              padding: '4px 10px', border: '1px solid',
              borderColor: template === 'img-right' ? '#3cbf9a' : '#e2e8f0',
              borderRadius: 6, background: template === 'img-right' ? '#f0fdf4' : 'white',
              color: template === 'img-right' ? '#059669' : '#718096',
              cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600,
            }}
          >
            <MiniPreviewImgTextRight /> Img der
          </button>
        </div>
        <RatioSelector value={ratio} onChange={(r) => onUpdate({ ...block, content: { ...content, ratio: r } })} />
      </div>

      {/* Content grid */}
      <div style={{ display: 'grid', gridTemplateColumns: gridCols, gap: 16 }}>
        {template === 'img-left' ? (
          <>
            {imageSection}
            {textSection}
          </>
        ) : (
          <>
            {textSection}
            {imageSection}
          </>
        )}
      </div>
    </div>
  );
};

// ── Dos Columnas Block Editor ────────────────────────────────────────

const DosColumnasBlockEditor = ({ block, onUpdate }) => {
  const content = block.content || { left: '', right: '', ratio: '50-50' };
  const ratio = content.ratio || '50-50';
  const gridCols = ratioToGrid(ratio);

  return (
    <div>
      <div style={{ marginBottom: 10, padding: '6px 10px', background: '#f8fafb', borderRadius: 8 }}>
        <RatioSelector value={ratio} onChange={(r) => onUpdate({ ...block, content: { ...content, ratio: r } })} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: gridCols, gap: 0 }}>
        <div style={{ paddingRight: 12 }}>
          <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#718096', marginBottom: 6 }}>Columna izquierda</div>
          <EditableContent
            value={content.left || ''}
            onChange={html => onUpdate({ ...block, content: { ...content, left: html } })}
            placeholder="Contenido izquierda..."
            minHeight={80}
          />
        </div>
        <div style={{ paddingLeft: 12, borderLeft: '1px solid #e2e8f0' }}>
          <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#718096', marginBottom: 6 }}>Columna derecha</div>
          <EditableContent
            value={content.right || ''}
            onChange={html => onUpdate({ ...block, content: { ...content, right: html } })}
            placeholder="Contenido derecha..."
            minHeight={80}
          />
        </div>
      </div>
    </div>
  );
};

const DocumentoBlockEditor = ({ block, onUpdate, token }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const content = block.content || { url: '', nombre: '', tamano: 0 };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API_URL}/api/blog/admin/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Error al subir');
      onUpdate({ ...block, content: { url: data.url, nombre: data.nombre_original, tamano: data.tamano } });
    } catch (err) {
      alert('Error al subir documento: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      {content.url ? (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px',
          background: '#f8fafb', borderRadius: 8, border: '1px solid #e2e8f0',
        }}>
          <div style={{ width: 44, height: 44, background: '#e2e8f0', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b' }}>
            <FileText size={22} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, color: '#2d3748', fontSize: '0.9rem' }}>{content.nombre || 'Documento'}</div>
            {content.tamano > 0 && <div style={{ color: '#a0aec0', fontSize: '0.78rem' }}>{(content.tamano / 1024).toFixed(0)} KB</div>}
          </div>
          <button
            type="button"
            onClick={() => { if (fileRef.current) fileRef.current.click(); }}
            style={{ padding: '6px 12px', border: '1px solid #e2e8f0', borderRadius: 6, background: 'white', cursor: 'pointer', fontSize: '0.82rem', color: '#4a5568' }}
          >
            Cambiar
          </button>
        </div>
      ) : (
        <div
          onClick={() => fileRef.current && fileRef.current.click()}
          style={{
            border: '2px dashed #e2e8f0', borderRadius: 8, padding: '32px 20px',
            textAlign: 'center', cursor: 'pointer', color: '#a0aec0',
            background: '#fafbfc',
          }}
          onMouseEnter={e => e.currentTarget.style.borderColor = '#3cbf9a'}
          onMouseLeave={e => e.currentTarget.style.borderColor = '#e2e8f0'}
        >
          {uploading ? (
            <div><div className="blog-spinner" style={{ display: 'inline-block', width: 24, height: 24, borderWidth: 3 }} /> <p style={{ marginTop: 8 }}>Subiendo documento...</p></div>
          ) : (
            <div>
              <FileUp size={32} style={{ marginBottom: 8 }} />
              <p style={{ fontWeight: 500 }}>Haz clic para subir un documento</p>
              <small>PDF, DOC, DOCX</small>
            </div>
          )}
        </div>
      )}
      <input ref={fileRef} type="file" accept=".pdf,.doc,.docx" onChange={handleUpload} style={{ display: 'none' }} />
    </div>
  );
};

const SeparadorBlockEditor = () => (
  <div style={{ padding: '16px 0' }}>
    <hr style={{ border: 'none', borderTop: '2px solid #e2e8f0' }} />
  </div>
);

// ── Block Type Labels ─────────────────────────────────────────────────

const blockTypeLabels = {
  texto: { label: 'Texto', icon: <Type size={13} />, color: '#3b82f6' },
  imagen: { label: 'Imagen', icon: <Image size={13} />, color: '#8b5cf6' },
  'imagen-texto': { label: 'Imagen + Texto', icon: <LayoutPanelLeft size={13} />, color: '#14b8a6' },
  'dos-columnas': { label: 'Dos columnas', icon: <Columns size={13} />, color: '#f59e0b' },
  documento: { label: 'Documento', icon: <FileText size={13} />, color: '#ef4444' },
  separador: { label: 'Separador', icon: <Minus size={13} />, color: '#6b7280' },
};

// ── Preview Renderer ──────────────────────────────────────────────────

const BlockPreview = ({ blocks }) => (
  <div className="blog-post-body" style={{ maxWidth: 780, margin: '0 auto' }}>
    {blocks.map(block => {
      switch (block.type) {
        case 'texto': {
          const size = (block.settings && block.settings.fontSize) || 'normal';
          return (
            <div
              key={block.id}
              className={`blog-block blog-block-text blog-text-${size}`}
              style={{ fontSize: fontSizeMap[size] || '1rem' }}
              dangerouslySetInnerHTML={{ __html: block.content || '' }}
            />
          );
        }
        case 'imagen': {
          const c = block.content || {};
          const alignStyle = {};
          if (c.align === 'left') alignStyle.textAlign = 'left';
          else if (c.align === 'right') alignStyle.textAlign = 'right';
          else alignStyle.textAlign = 'center';
          const widthVal = c.width || 100;
          return (
            <div key={block.id} className={`blog-block blog-block-image align-${c.align || 'center'}`} style={{ ...alignStyle, margin: '20px 0', maxWidth: `${widthVal}%`, marginLeft: c.align === 'center' ? 'auto' : undefined, marginRight: c.align === 'center' ? 'auto' : undefined }}>
              {c.url && <img src={c.url} alt={c.caption || ''} style={{ maxWidth: '100%', width: c.align === 'full' ? '100%' : 'auto', borderRadius: 10 }} />}
              {c.caption && <p style={{ color: '#718096', fontSize: '0.88rem', marginTop: 8, fontStyle: 'italic' }}>{c.caption}</p>}
            </div>
          );
        }
        case 'imagen-texto': {
          const c = block.content || {};
          const template = c.template || 'img-left';
          const ratio = c.ratio || '40-60';
          const gridCols = ratioToGrid(ratio);
          const imageEl = (
            <div className="layout-image">
              {c.image && c.image.url && <img src={c.image.url} alt={(c.image && c.image.caption) || ''} style={{ width: '100%', borderRadius: 10, objectFit: 'cover' }} />}
              {c.image && c.image.caption && <p style={{ color: '#718096', fontSize: '0.85rem', marginTop: 6, fontStyle: 'italic' }}>{c.image.caption}</p>}
            </div>
          );
          const textEl = (
            <div className="layout-text" dangerouslySetInnerHTML={{ __html: c.text || '' }} />
          );
          return (
            <div key={block.id} className={`blog-block blog-layout-img-text layout-${template} ratio-${ratio}`} style={{ display: 'grid', gridTemplateColumns: gridCols, gap: 24, margin: '20px 0', alignItems: 'start' }}>
              {template === 'img-left' ? <>{imageEl}{textEl}</> : <>{textEl}{imageEl}</>}
            </div>
          );
        }
        case 'dos-columnas': {
          const c = block.content || {};
          const ratio = c.ratio || '50-50';
          const gridCols = ratioToGrid(ratio);
          return (
            <div key={block.id} className={`blog-block blog-block-columns ratio-${ratio} with-separator`} style={{ display: 'grid', gridTemplateColumns: gridCols, gap: 0, margin: '20px 0' }}>
              <div className="blog-column-left" style={{ paddingRight: 16 }} dangerouslySetInnerHTML={{ __html: c.left || '' }} />
              <div className="blog-column-right" style={{ paddingLeft: 16, borderLeft: '1px solid #e2e8f0' }} dangerouslySetInnerHTML={{ __html: c.right || '' }} />
            </div>
          );
        }
        case 'documento': {
          const c = block.content || {};
          return (
            <div key={block.id} className="blog-block blog-block-document" style={{ margin: '16px 0' }}>
              <a href={c.url} target="_blank" rel="noopener noreferrer" style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: '14px 18px',
                background: '#f8fafb', border: '1px solid #e2e8f0', borderRadius: 10,
                textDecoration: 'none', color: '#2d3748', transition: 'all 0.2s',
              }}>
                <div style={{ width: 40, height: 40, background: '#e2e8f0', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b' }}>
                  <FileText size={20} />
                </div>
                <div>
                  <div style={{ fontWeight: 500, fontSize: '0.9rem' }}>{c.nombre || 'Documento'}</div>
                  {c.tamano > 0 && <div style={{ color: '#a0aec0', fontSize: '0.78rem' }}>{(c.tamano / 1024).toFixed(0)} KB</div>}
                </div>
              </a>
            </div>
          );
        }
        case 'separador':
          return <div key={block.id} className="blog-block blog-block-separator" style={{ margin: '24px 0' }}><hr style={{ border: 'none', borderTop: '2px solid #e2e8f0' }} /></div>;
        default:
          return null;
      }
    })}
  </div>
);

// ════════════════════════════════════════════════════════════════════════
//  MAIN COMPONENT
// ════════════════════════════════════════════════════════════════════════

const BlogAdmin = () => {
  const { user } = useContext(AuthContext);
  const { theme } = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const fileInputRef = useRef(null);

  const [posts, setPosts] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Form state
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    id: null,
    titulo: '',
    extracto: '',
    contenido: '',
    categoria_id: '',
    etiquetas: '',
    estado: 'borrador',
    exclusivo_clientes: 0
  });
  const [newFiles, setNewFiles] = useState([]);
  const [existingFiles, setExistingFiles] = useState([]);
  const [filesToDelete, setFilesToDelete] = useState([]);

  // Block editor state
  const [blocks, setBlocks] = useState([createBlock('texto')]);
  const [previewMode, setPreviewMode] = useState(false);

  // New category
  const [showNewCat, setShowNewCat] = useState(false);
  const [newCatName, setNewCatName] = useState('');
  const [newCatColor, setNewCatColor] = useState('#3cbf9a');

  const token = localStorage.getItem('token');

  // Auto-open edit form if URL has ?edit=id
  useEffect(() => {
    const editId = searchParams.get('edit');
    if (editId && posts.length > 0) {
      const post = posts.find(p => p.id === parseInt(editId));
      if (post) openEdit(post);
    }
  }, [searchParams, posts]);

  // Fetch posts
  const fetchPosts = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/blog/admin/posts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setPosts(data.posts || []);
    } catch (err) {
      console.error('Error fetching admin posts:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch categories
  const fetchCategorias = async () => {
    try {
      const res = await fetch(`${API_URL}/api/blog/categorias`);
      const data = await res.json();
      setCategorias(data.categorias || []);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  useEffect(() => {
    fetchPosts();
    fetchCategorias();
  }, []);

  const showMessage = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 4000);
  };

  const resetForm = () => {
    setEditMode(false);
    setFormData({
      id: null, titulo: '', extracto: '', contenido: '',
      categoria_id: '', etiquetas: '', estado: 'borrador', exclusivo_clientes: 0
    });
    setNewFiles([]);
    setExistingFiles([]);
    setFilesToDelete([]);
    setBlocks([createBlock('texto')]);
    setPreviewMode(false);
    setSearchParams({});
  };

  const openNew = () => {
    resetForm();
    setEditMode(true);
  };

  const openEdit = (post) => {
    setFormData({
      id: post.id,
      titulo: post.titulo,
      extracto: post.extracto || '',
      contenido: post.contenido || '',
      categoria_id: post.categoria_id || '',
      etiquetas: post.etiquetas || '',
      estado: post.estado,
      exclusivo_clientes: post.exclusivo_clientes
    });
    setExistingFiles(post.archivos || []);
    setNewFiles([]);
    setFilesToDelete([]);
    setBlocks(parseContentToBlocks(post.contenido || ''));
    setPreviewMode(false);
    setEditMode(true);
  };

  // Legacy file handling
  const handleFileChange = (e) => {
    const selected = Array.from(e.target.files);
    setNewFiles(prev => [...prev, ...selected]);
  };

  const removeNewFile = (index) => {
    setNewFiles(prev => prev.filter((_, i) => i !== index));
  };

  const markFileForDeletion = (fileId) => {
    setFilesToDelete(prev => [...prev, fileId]);
    setExistingFiles(prev => prev.filter(f => f.id !== fileId));
  };

  // ── Block operations ──────────────────────────────────────────────

  const addBlock = useCallback((type, afterIndex, blockOverride) => {
    const newBlock = blockOverride || createBlock(type);
    setBlocks(prev => {
      const idx = afterIndex !== undefined ? afterIndex + 1 : prev.length;
      const next = [...prev];
      next.splice(idx, 0, newBlock);
      return next;
    });
  }, []);

  const updateBlock = useCallback((updatedBlock) => {
    setBlocks(prev => prev.map(b => b.id === updatedBlock.id ? updatedBlock : b));
  }, []);

  const deleteBlock = useCallback((blockId) => {
    setBlocks(prev => {
      const next = prev.filter(b => b.id !== blockId);
      return next.length === 0 ? [createBlock('texto')] : next;
    });
  }, []);

  const moveBlock = useCallback((index, direction) => {
    setBlocks(prev => {
      const next = [...prev];
      const newIdx = index + direction;
      if (newIdx < 0 || newIdx >= next.length) return prev;
      [next[index], next[newIdx]] = [next[newIdx], next[index]];
      return next;
    });
  }, []);

  // ── Submit ────────────────────────────────────────────────────────

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.titulo.trim()) {
      showMessage('El titulo es obligatorio', 'error');
      return;
    }

    setSaving(true);
    try {
      // Generate contenido from blocks
      const html = blocksToHtml(blocks);
      const blocksComment = encodeBlocks(blocks);
      const contenido = blocksComment + '\n' + html;

      const fd = new FormData();
      fd.append('titulo', formData.titulo);
      fd.append('extracto', formData.extracto);
      fd.append('contenido', contenido);
      fd.append('categoria_id', formData.categoria_id);
      fd.append('etiquetas', formData.etiquetas);
      fd.append('estado', formData.estado);
      fd.append('exclusivo_clientes', formData.exclusivo_clientes);

      if (filesToDelete.length > 0) {
        fd.append('archivos_eliminar', JSON.stringify(filesToDelete));
      }

      newFiles.forEach(file => {
        fd.append('archivos', file);
      });

      const isUpdate = formData.id !== null;
      const url = isUpdate
        ? `${API_URL}/api/blog/admin/posts/${formData.id}`
        : `${API_URL}/api/blog/admin/posts`;

      const res = await fetch(url, {
        method: isUpdate ? 'PUT' : 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Error al guardar');

      showMessage(isUpdate ? 'Publicacion actualizada correctamente' : 'Publicacion creada correctamente');
      resetForm();
      fetchPosts();
    } catch (err) {
      showMessage(err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (postId) => {
    try {
      const res = await fetch(`${API_URL}/api/blog/admin/posts/${postId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error();
      showMessage('Publicacion eliminada');
      setDeleteConfirm(null);
      fetchPosts();
    } catch (err) {
      showMessage('Error al eliminar', 'error');
    }
  };

  const handleCreateCategory = async () => {
    if (!newCatName.trim()) return;
    try {
      const res = await fetch(`${API_URL}/api/blog/admin/categorias`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ nombre: newCatName, color: newCatColor })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setNewCatName('');
      setShowNewCat(false);
      fetchCategorias();
      showMessage('Categoria creada');
    } catch (err) {
      showMessage(err.message, 'error');
    }
  };

  const filteredPosts = posts.filter(p =>
    p.titulo.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (p.extracto && p.extracto.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const estadoStyles = {
    borrador: { bg: '#fef3c7', color: '#92400e', label: 'Borrador' },
    publicado: { bg: '#d1fae5', color: '#065f46', label: 'Publicado' },
    archivado: { bg: '#e5e7eb', color: '#374151', label: 'Archivado' }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';

  // ── Block Editor Render ───────────────────────────────────────────

  const renderBlockEditor = (block, index) => {
    const typeInfo = blockTypeLabels[block.type] || blockTypeLabels.texto;

    return (
      <div
        key={block.id}
        style={{
          position: 'relative',
          border: '1px solid #e8ecf0',
          borderRadius: 10,
          background: 'white',
          marginBottom: 0,
          transition: 'box-shadow 0.2s, border-color 0.2s',
        }}
        className="block-editor-wrapper"
        onMouseEnter={e => {
          e.currentTarget.style.borderColor = '#cbd5e0';
          e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.06)';
          const ctrl = e.currentTarget.querySelector('.block-controls');
          if (ctrl) ctrl.style.opacity = '1';
        }}
        onMouseLeave={e => {
          e.currentTarget.style.borderColor = '#e8ecf0';
          e.currentTarget.style.boxShadow = 'none';
          const ctrl = e.currentTarget.querySelector('.block-controls');
          if (ctrl) ctrl.style.opacity = '0';
        }}
      >
        {/* Block type label */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 6, padding: '8px 12px',
          borderBottom: '1px solid #f1f5f9', fontSize: '0.78rem', fontWeight: 600,
          color: typeInfo.color, userSelect: 'none',
        }}>
          {typeInfo.icon}
          {typeInfo.label}
        </div>

        <BlockControls
          index={index}
          total={blocks.length}
          onMoveUp={() => moveBlock(index, -1)}
          onMoveDown={() => moveBlock(index, 1)}
          onDelete={() => deleteBlock(block.id)}
        />

        <div style={{ padding: block.type === 'separador' ? '0 12px' : 12 }}>
          {block.type === 'texto' && <TextoBlockEditor block={block} onUpdate={updateBlock} />}
          {block.type === 'imagen' && <ImagenBlockEditor block={block} onUpdate={updateBlock} token={token} />}
          {block.type === 'imagen-texto' && <ImagenTextoBlockEditor block={block} onUpdate={updateBlock} token={token} />}
          {block.type === 'dos-columnas' && <DosColumnasBlockEditor block={block} onUpdate={updateBlock} />}
          {block.type === 'documento' && <DocumentoBlockEditor block={block} onUpdate={updateBlock} token={token} />}
          {block.type === 'separador' && <SeparadorBlockEditor />}
        </div>
      </div>
    );
  };

  // Helper for AddBlockMenu integration
  const handleAddBlock = useCallback((type, afterIndex) => {
    return (blockType, blockOverride) => {
      if (blockOverride && typeof blockOverride === 'object' && blockOverride.id) {
        // A pre-created block was passed (e.g., imagen-texto with variant)
        addBlock(blockType, afterIndex, blockOverride);
      } else {
        addBlock(blockType, afterIndex);
      }
    };
  }, [addBlock]);

  // ── Edit form view ────────────────────────────────────────────────

  if (editMode) {
    return (
      <div className="blog-admin-page">
        <div className="blog-admin-header">
          <button onClick={resetForm} className="blog-admin-back">
            <ArrowLeft size={18} /> Volver a la lista
          </button>
          <h1>{formData.id ? 'Editar publicacion' : 'Nueva publicacion'}</h1>
        </div>

        {message.text && (
          <div className={`blog-admin-message ${message.type}`}>
            {message.type === 'error' ? <AlertCircle size={16} /> : <CheckCircle size={16} />}
            {message.text}
          </div>
        )}

        <form onSubmit={handleSubmit} className="blog-admin-form">
          <div className="blog-form-main">
            {/* Title */}
            <div className="blog-form-group">
              <label>Titulo *</label>
              <input
                type="text"
                value={formData.titulo}
                onChange={e => setFormData(prev => ({ ...prev, titulo: e.target.value }))}
                placeholder="Titulo de la publicacion"
                className="blog-form-input title-input"
              />
            </div>

            {/* Excerpt */}
            <div className="blog-form-group">
              <label>Extracto</label>
              <textarea
                value={formData.extracto}
                onChange={e => setFormData(prev => ({ ...prev, extracto: e.target.value }))}
                placeholder="Resumen breve de la publicacion (se muestra en la tarjeta del blog)"
                rows={3}
                className="blog-form-input"
              />
            </div>

            {/* Block Editor / Preview Toggle */}
            <div className="blog-form-group">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                <label style={{ margin: 0 }}>Contenido</label>
                <button
                  type="button"
                  onClick={() => setPreviewMode(!previewMode)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    padding: '6px 14px', border: '1px solid',
                    borderColor: previewMode ? '#3cbf9a' : '#e2e8f0',
                    borderRadius: 6, background: previewMode ? '#f0fdf4' : 'white',
                    color: previewMode ? '#3cbf9a' : '#4a5568',
                    cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600, transition: 'all 0.2s',
                  }}
                >
                  <Eye size={14} />
                  {previewMode ? 'Editar' : 'Vista previa'}
                </button>
              </div>

              {previewMode ? (
                <div style={{
                  border: '1px solid #e2e8f0', borderRadius: 10, padding: '24px 20px',
                  background: '#fafbfc', minHeight: 200,
                }}>
                  {blocks.length === 0 || (blocks.length === 1 && !blocks[0].content) ? (
                    <div style={{ textAlign: 'center', color: '#a0aec0', padding: 40 }}>
                      <Eye size={32} style={{ marginBottom: 8 }} />
                      <p>No hay contenido para previsualizar</p>
                    </div>
                  ) : (
                    <BlockPreview blocks={blocks} />
                  )}
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <AddBlockMenu onAdd={(type, blockOverride) => {
                    if (blockOverride && blockOverride.id) {
                      setBlocks(prev => {
                        const next = [...prev];
                        next.splice(0, 0, blockOverride);
                        return next;
                      });
                    } else {
                      addBlock(type, -1);
                    }
                  }} />

                  {blocks.map((block, index) => (
                    <React.Fragment key={block.id}>
                      {renderBlockEditor(block, index)}
                      <AddBlockMenu onAdd={(type, blockOverride) => {
                        if (blockOverride && blockOverride.id) {
                          setBlocks(prev => {
                            const next = [...prev];
                            next.splice(index + 1, 0, blockOverride);
                            return next;
                          });
                        } else {
                          addBlock(type, index);
                        }
                      }} />
                    </React.Fragment>
                  ))}
                </div>
              )}
            </div>

            {/* Legacy Files Section */}
            <div className="blog-form-group" style={{ borderTop: '1px solid #e2e8f0', paddingTop: 16, marginTop: 8 }}>
              <label><Image size={16} /> Archivos adjuntos (sistema anterior)</label>

              {existingFiles.length > 0 && (
                <div className="blog-form-files-existing">
                  {existingFiles.map(file => (
                    <div key={file.id} className="blog-file-item">
                      {file.es_imagen ? (
                        <img src={file.url} alt={file.nombre_original} className="blog-file-thumb" />
                      ) : (
                        <div className="blog-file-icon"><FileText size={20} /></div>
                      )}
                      <span className="blog-file-name">{file.nombre_original}</span>
                      <button
                        type="button"
                        onClick={() => markFileForDeletion(file.id)}
                        className="blog-file-remove"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {newFiles.length > 0 && (
                <div className="blog-form-files-new">
                  {newFiles.map((file, idx) => (
                    <div key={idx} className="blog-file-item new">
                      {file.type.startsWith('image/') ? (
                        <img src={URL.createObjectURL(file)} alt={file.name} className="blog-file-thumb" />
                      ) : (
                        <div className="blog-file-icon"><FileText size={20} /></div>
                      )}
                      <span className="blog-file-name">{file.name}</span>
                      <span className="blog-file-size">{(file.size / 1024).toFixed(0)} KB</span>
                      <button type="button" onClick={() => removeNewFile(idx)} className="blog-file-remove">
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <button
                type="button"
                onClick={() => fileInputRef.current.click()}
                className="blog-upload-btn"
              >
                <Upload size={16} /> Anadir archivos
              </button>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*,.pdf,.doc,.docx"
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
              <span className="blog-form-hint">La primera imagen se usara como portada. Maximo 15MB por archivo.</span>
            </div>
          </div>

          {/* Sidebar settings */}
          <div className="blog-form-sidebar">
            {/* Status */}
            <div className="blog-form-group">
              <label>Estado</label>
              <select
                value={formData.estado}
                onChange={e => setFormData(prev => ({ ...prev, estado: e.target.value }))}
                className="blog-form-input"
              >
                <option value="borrador">Borrador</option>
                <option value="publicado">Publicado</option>
                <option value="archivado">Archivado</option>
              </select>
            </div>

            {/* Category */}
            <div className="blog-form-group">
              <label><Folder size={14} /> Categoria</label>
              <select
                value={formData.categoria_id}
                onChange={e => setFormData(prev => ({ ...prev, categoria_id: e.target.value }))}
                className="blog-form-input"
              >
                <option value="">Sin categoria</option>
                {categorias.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.nombre}</option>
                ))}
              </select>
              <button type="button" onClick={() => setShowNewCat(!showNewCat)} className="blog-link-btn">
                <Plus size={14} /> Nueva categoria
              </button>
              {showNewCat && (
                <div className="blog-new-cat-form">
                  <input
                    type="text"
                    value={newCatName}
                    onChange={e => setNewCatName(e.target.value)}
                    placeholder="Nombre de la categoria"
                    className="blog-form-input"
                  />
                  <div className="blog-new-cat-row">
                    <input
                      type="color"
                      value={newCatColor}
                      onChange={e => setNewCatColor(e.target.value)}
                      className="blog-color-input"
                    />
                    <button type="button" onClick={handleCreateCategory} className="blog-btn-small">Crear</button>
                  </div>
                </div>
              )}
            </div>

            {/* Tags */}
            <div className="blog-form-group">
              <label><Tag size={14} /> Etiquetas</label>
              <input
                type="text"
                value={formData.etiquetas}
                onChange={e => setFormData(prev => ({ ...prev, etiquetas: e.target.value }))}
                placeholder="ej: salud, ofertas, novedades"
                className="blog-form-input"
              />
              <span className="blog-form-hint">Separadas por comas</span>
            </div>

            {/* Exclusive */}
            <div className="blog-form-group">
              <label className="blog-checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.exclusivo_clientes === 1}
                  onChange={e => setFormData(prev => ({
                    ...prev,
                    exclusivo_clientes: e.target.checked ? 1 : 0
                  }))}
                />
                <Lock size={14} />
                Exclusivo para clientes
              </label>
              <span className="blog-form-hint">Solo visible para usuarios con cuenta de cliente o admin</span>
            </div>

            {/* Block count info */}
            <div style={{
              padding: '10px 12px', background: '#f8fafb', borderRadius: 8,
              fontSize: '0.82rem', color: '#718096',
            }}>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>Bloques de contenido</div>
              {blocks.map((b, i) => {
                const info = blockTypeLabels[b.type] || blockTypeLabels.texto;
                return (
                  <div key={b.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '2px 0' }}>
                    <span style={{ color: info.color }}>{info.icon}</span>
                    <span>{info.label}</span>
                  </div>
                );
              })}
            </div>

            {/* Actions */}
            <div className="blog-form-actions">
              <button type="submit" className="blog-btn-primary" disabled={saving}>
                {saving ? (
                  <><div className="blog-spinner" /> Guardando...</>
                ) : (
                  <><Save size={16} /> {formData.id ? 'Actualizar' : 'Crear publicacion'}</>
                )}
              </button>
              <button type="button" onClick={resetForm} className="blog-btn-secondary">
                Cancelar
              </button>
            </div>
          </div>
        </form>
      </div>
    );
  }

  // ── List view ─────────────────────────────────────────────────────

  return (
    <div className="blog-admin-page">
      <div className="blog-admin-header">
        <h1><BookOpen size={24} /> Gestion del Blog</h1>
        <button onClick={openNew} className="blog-btn-primary">
          <Plus size={16} /> Nueva publicacion
        </button>
      </div>

      {message.text && (
        <div className={`blog-admin-message ${message.type}`}>
          {message.type === 'error' ? <AlertCircle size={16} /> : <CheckCircle size={16} />}
          {message.text}
        </div>
      )}

      {/* Search */}
      <div className="blog-admin-search">
        <Search size={18} />
        <input
          type="text"
          placeholder="Buscar publicaciones..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
        />
      </div>

      {/* Posts table */}
      {loading ? (
        <div className="blog-admin-loading">Cargando publicaciones...</div>
      ) : filteredPosts.length === 0 ? (
        <div className="blog-admin-empty">
          <BookOpen size={40} />
          <p>{searchTerm ? 'No se encontraron publicaciones' : 'No hay publicaciones aun'}</p>
          <button onClick={openNew} className="blog-btn-primary"><Plus size={16} /> Crear primera publicacion</button>
        </div>
      ) : (
        <div className="blog-admin-table-wrapper">
          <table className="blog-admin-table">
            <thead>
              <tr>
                <th>Publicacion</th>
                <th>Categoria</th>
                <th>Estado</th>
                <th>Fecha</th>
                <th>Vistas</th>
                <th>Archivos</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filteredPosts.map(post => {
                const est = estadoStyles[post.estado] || estadoStyles.borrador;
                return (
                  <tr key={post.id}>
                    <td className="blog-admin-post-cell">
                      <div className="blog-admin-post-info">
                        {post.imagen_portada && (
                          <img src={post.imagen_portada} alt="" className="blog-admin-thumb" />
                        )}
                        <div>
                          <strong>{post.titulo}</strong>
                          {post.exclusivo_clientes === 1 && (
                            <span className="blog-admin-exclusive"><Lock size={10} /> Exclusivo</span>
                          )}
                          {post.extracto && <p className="blog-admin-excerpt">{post.extracto.substring(0, 80)}...</p>}
                        </div>
                      </div>
                    </td>
                    <td>
                      {post.categoria_nombre && (
                        <span className="blog-admin-cat" style={{ backgroundColor: post.categoria_color }}>
                          {post.categoria_nombre}
                        </span>
                      )}
                    </td>
                    <td>
                      <span className="blog-admin-status" style={{ backgroundColor: est.bg, color: est.color }}>
                        {est.label}
                      </span>
                    </td>
                    <td className="blog-admin-date">{formatDate(post.published_at || post.created_at)}</td>
                    <td>{post.vistas}</td>
                    <td>{post.archivos?.length || 0}</td>
                    <td>
                      <div className="blog-admin-actions">
                        <button onClick={() => openEdit(post)} title="Editar" className="blog-action-btn edit">
                          <Edit size={15} />
                        </button>
                        {post.estado === 'publicado' && (
                          <a href={`/blog/${post.slug}`} target="_blank" rel="noopener noreferrer" title="Ver" className="blog-action-btn view">
                            <Eye size={15} />
                          </a>
                        )}
                        {deleteConfirm === post.id ? (
                          <div className="blog-delete-confirm">
                            <button onClick={() => handleDelete(post.id)} className="blog-action-btn delete">Si</button>
                            <button onClick={() => setDeleteConfirm(null)} className="blog-action-btn cancel">No</button>
                          </div>
                        ) : (
                          <button onClick={() => setDeleteConfirm(post.id)} title="Eliminar" className="blog-action-btn delete">
                            <Trash2 size={15} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default BlogAdmin;
